import { useEffect, useRef, useState } from "react";
import MolecularStructure from "@/components/charts/MolecularStructure";
import ReferenceSpectrum from "@/components/charts/ReferenceSpectrum";
import type {
  ReferenceSpectrumPeak,
  SmilesProvenance,
  StereoState,
} from "@/types/referenceCompound";
import {
  computeMolecularIdentifiers,
  type MolecularIdentifiers,
} from "@/utils/molecularIdentifiers";
import { panelCls } from "@/utils/panelCls";

/**
 * 선택 물질 상세 패널 — 2단 화면의 오른쪽 (2026-08-20 통일).
 *
 * 기획 v2 §1.3 원본(NIST MS Search) Names 화면의 우측 패널을 참조물질 DB와
 * 표준 라이브러리가 **같은 골격으로** 쓴다. 종전에는 참조물질 DB만 2단이고
 * 표준 라이브러리는 표+모달이었다 — 같은 성격의 화면이 다른 조작을 요구했다.
 *
 * 데이터셋에 따라 **없는 것을 다르게 말한다**:
 *
 * * `reference` (중앙대 실측 125종) — 스펙트럼이 있으면 그린다. Synonyms·RI 는
 *   연구진 회신 대기.
 * * `standard-library` (표준 라이브러리 93만종) — 이름·분자식·SMILES 만 있는
 *   자료다. 스펙트럼·CAS·Synonyms·RI 가 **애초에 없으므로** '회신 대기'가
 *   아니라 '이 라이브러리 미수록'이라고 말한다. 빈 차트는 그리지 않는다.
 *
 * 두 경우 모두 SMILES 가 있으면 2D 구조와 InChIKey·Exact Mass 는 계산해서
 * 보여준다 — 계산값임을 함께 밝힌다.
 *
 * **SMILES 자체의 출처도 밝힌다** (2026-08-22 sidecar 보강분). 표준 라이브러리
 * 93만종 중 212,458건은 원 자산에 SMILES 가 없었고, PubChem 이름 대조와 OPSIN
 * 이름→구조 계산으로 채웠다. 채운 값은 연구진 검증 전 제안값이므로 어느 경로로
 * 왔는지, 입체 표기를 손댔는지를 상세 화면이 말한다. 출처 컬럼이 없는 구
 * sidecar 는 값이 오지 않으므로 **아무 줄도 나오지 않는다** — 부재는 오류가
 * 아니라 미상이다.
 */

/** 격자 한 행에 담는 `m/z 상대강도` 쌍 수 (원본과 같은 배치). */
const PAIRS_PER_ROW = 4;

/** 필드 한 줄로 쓸 표기 — 값과 괄호 보조문구. */
interface FieldText {
  text: string;
  note?: string;
}

/**
 * 두 보강 경로(PubChem 대조·OPSIN 계산)로 채운 SMILES 에 붙는 공통 사실.
 * 둘 다 원본 분자식과 일치하는 것만 채택했지만 **연구진 검증 전**이다.
 */
const PROPOSED_NOTE = "분자식 일치분 · 연구진 검증 전 제안값";

/** 어휘에 없는 출처 표기 — 값은 보여 주되 확정값으로 읽히지 않게 한다. */
const UNKNOWN_SOURCE_NOTE = "알 수 없는 출처 표기 · 연구진 검증 전";

/**
 * `smiles_source` 를 사람 말로 옮긴다.
 *
 * 값이 없으면(구 sidecar 는 컬럼 자체가 없다) `null` — 화면은 출처 줄을 아예
 * 두지 않는다. 모르는 값은 해석하지 않고 그대로 보여 준다: 새 경로가 추가돼도
 * 프론트가 없는 사실을 말하거나 값을 삼키지 않게.
 */
export function describeSmilesSource(
  raw?: SmilesProvenance | null,
): FieldText | null {
  const value = (raw ?? "").trim();
  if (!value) return null;
  // 대소문자를 무시하고 비교한다 — 저장소의 기존 SMILES 파이프라인
  // (`scripts/smiles_enrich/merge_smiles.py`)은 대문자 어휘(OPSIN/PubChem)를 쓴다.
  const key = value.toLowerCase();
  if (key === "library") return { text: "원 자산 수록" };
  if (key === "pubchem")
    return { text: "PubChem 이름 대조", note: PROPOSED_NOTE };
  if (key === "opsin" || key.startsWith("opsin:")) {
    const rule = value.slice("opsin:".length).trim();
    return {
      text: rule ? `이름에서 계산 (OPSIN · ${rule})` : "이름에서 계산 (OPSIN)",
      note: PROPOSED_NOTE,
    };
  }
  // 모르는 표기는 **원 자산 보유분으로 승격하지 않는다.** 값은 그대로 보여
  // 주되 검증 전이라는 사실을 유지한다 — 모르는 출처를 확정값처럼 보이게 하는
  // 쪽이 훨씬 위험하다 (2026-08-22 교차 검토 MEDIUM).
  return { text: value, note: UNKNOWN_SOURCE_NOTE };
}

/**
 * `stereo_state` 를 사람 말로 옮긴다.
 *
 * `stereo-removed` 는 **구조도만 봐서는 알 수 없는 사실**이다 — 원본 이름이
 * 라세미·상대배치였는데 계산 결과가 절대배치로 나와 입체 표기를 지운 값이다.
 * 괄호 안 사유는 sidecar 가 적어 준 원문 그대로 덧붙인다.
 */
export function describeStereoState(
  raw?: StereoState | null,
): FieldText | null {
  const value = (raw ?? "").trim();
  if (!value) return null;
  if (value === "stereo-unspecified") return { text: "입체 미지정" };
  if (value === "stereo-removed" || value.startsWith("stereo-removed(")) {
    // 괄호 안에 사유가 적혀 오면 **sidecar 가 쓴 말 그대로** 쓴다. 없을 때만
    // 우리가 아는 사유(라세미·상대배치)를 대신 적는다 — 같은 말을 두 번 적어
    // 필드를 늘리지 않기 위해서다.
    const detail =
      value.startsWith("stereo-removed(") && value.endsWith(")")
        ? value.slice("stereo-removed(".length, -1).trim()
        : "";
    return {
      text: "입체 표기 제거",
      note: detail || "원본 이름이 라세미·상대배치",
    };
  }
  return { text: value };
}

export type DetailDataset = "reference" | "standard-library";

export interface DetailPaneCompound {
  name: string;
  nameKo?: string | null;
  formula?: string | null;
  casNumber?: string | null;
  compoundClass?: string | null;
  /** 큐레이션 카테고리 (core/risk/scenario) — 있을 때만 보여 준다. */
  category?: string | null;
  /** 참조 DB 계층 (Sprint 23 tiering). */
  tier?: string | null;
  /**
   * 인화성 액체(ILR) 지시성분 표시. 표에서 '촉진제' 열로 보여 주던 자리인데,
   * 백엔드가 주는 필드는 `is_ignitable_liquid_indicator` 이고 '촉진제'는
   * 판정 어휘라 쓰지 않는다 (2026-08-20).
   */
  isIgnitableLiquidIndicator?: boolean | null;
  smiles?: string | null;
  /**
   * SMILES 출처 — sidecar `smiles_source`. 구 자산(컬럼 없음)에서는 undefined 로
   * 온다. 값이 없으면 화면은 출처를 **말하지 않는다** (지어내지 않는다).
   */
  smilesSource?: SmilesProvenance | null;
  /** 입체 표기 상태 — sidecar `stereo_state`. 없으면 마찬가지로 말하지 않는다. */
  stereoState?: StereoState | null;
  peaks?: ReferenceSpectrumPeak[];
  /** 스펙트럼 출처 라벨 — 출처 없는 스펙트럼은 근거가 못 된다. */
  spectrumSource?: string | null;
}

interface CompoundDetailPaneProps {
  compound: DetailPaneCompound | null;
  dataset: DetailDataset;
  /** 선택 전 안내 문구. */
  emptyMessage?: string;
  /**
   * 스펙트럼 조회가 진행 중이거나 이미 결과가 있음 (T6-3 후속, 2026-08-27
   * 적대적 리뷰 확증) — `standard-library` 에서만 의미가 있다. `peaks` 가
   * 아직 비어 있어도(조회 중) 이 값이 참이면 **최종 레이아웃(구조+스펙트럼
   * 2단)을 미리 잡아 둔다** — 구조-only(1단, 큰 구조도) → 스펙트럼+구조
   * (2단, 작은 구조도) 사이를 오가며 구조도(RDKit canvas)가 재마운트·
   * 재계산되는 것을 막는다. 생략하면(기본 false) 기존처럼 `peaks` 유무만
   * 본다.
   */
  spectrumPending?: boolean;
}

export default function CompoundDetailPane({
  compound,
  dataset,
  emptyMessage = "왼쪽 목록에서 물질을 선택하세요.",
  spectrumPending = false,
}: CompoundDetailPaneProps) {
  const [identifiers, setIdentifiers] = useState<MolecularIdentifiers | null>(
    null,
  );
  const [copied, setCopied] = useState(false);
  const smiles = compound?.smiles ?? null;

  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    [],
  );

  const handleCopySmiles = async () => {
    if (!smiles) return;
    try {
      await navigator.clipboard.writeText(smiles);
      setCopied(true);
      // 앞선 타이머가 새 '복사됨'을 일찍 지우지 않도록 매번 갈아 끼운다.
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // 클립보드 미지원 환경 — 조용히 무시
    }
  };

  // 의존성은 **SMILES 하나**다. 종전에는 `compound` 객체까지 넣었는데, 두 호출처
  // 모두 렌더마다 새 리터럴을 만들기 때문에 선택이 그대로여도 부모가 리렌더될
  // 때마다 식별자를 지우고 RDKit 을 다시 돌렸다 — 검색어 한 글자마다 Exact
  // Mass·InChIKey 가 "계산 중…" 으로 깜빡였다 (2026-08-20 교차 리뷰).
  useEffect(() => {
    let cancelled = false;
    setIdentifiers(null);
    setCopied(false);
    if (!smiles) {
      setIdentifiers({
        exactMass: null,
        inchiKey: null,
        error: "SMILES 미보유",
      });
      return;
    }
    computeMolecularIdentifiers(smiles).then((r) => {
      if (!cancelled) setIdentifiers(r);
    });
    return () => {
      cancelled = true;
    };
  }, [smiles]);

  if (!compound) {
    return (
      <div className="rounded border border-dashed border-gray-200 px-4 py-12 text-center text-sm text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  const isStandardLibrary = dataset === "standard-library";
  // 이 데이터셋에 애초에 없는 항목인지, 받기로 했는데 아직 없는 항목인지를
  // 구분해 말한다 — 둘은 다른 사실이다.
  const notHeld = isStandardLibrary
    ? "이 라이브러리 미수록"
    : "자료 미보유 (연구진 회신 대기)";

  const smilesSource = describeSmilesSource(compound.smilesSource);
  const stereoState = describeStereoState(compound.stereoState);

  const peaks = compound.peaks ?? [];
  const basePeak = peaks.reduce((m, p) => Math.max(m, p.intensity), 0);
  const sortedPeaks = [...peaks].sort((a, b) => a.mz - b.mz);
  const grouped: (typeof sortedPeaks)[] = [];
  for (let i = 0; i < sortedPeaks.length; i += PAIRS_PER_ROW) {
    grouped.push(sortedPeaks.slice(i, i + PAIRS_PER_ROW));
  }
  // 표준 라이브러리의 강도는 원자료 측정값이 아니라 sidecar 가 이미
  // 0–999(base peak=999) 로 정규화해 둔 값이다 — "측정값"이라는 말은 실측을
  // 가리키므로 이 데이터셋엔 맞지 않는다 (적대적 리뷰 확증, 2026-08-27).
  const intensityLabel = isStandardLibrary
    ? "라이브러리 강도(0–999)"
    : "측정값";
  // 조사 은/는 — "강도(0–999)"는 마지막 발음이 "구"(999→구백구십구)라 "는",
  // "측정값"은 받침 ㅄ 이라 "은"이 맞다.
  const intensityParticle = isStandardLibrary ? "는" : "은";
  // 아직 스펙트럼 조회 결과를 모르더라도(조회 중) 구조-only ↔ 스펙트럼+구조
  // 사이를 오가며 구조도가 재마운트되지 않도록 최종 레이아웃을 미리 잡는다.
  const showSpectrumLayout =
    !isStandardLibrary || peaks.length > 0 || spectrumPending;

  return (
    /* section = 이름 붙은 region 랜드마크 — 스크린리더가 상세 영역으로 옮겨갈 수 있다 */
    <section aria-label="선택 물질 상세">
      <h3 className="mb-2 text-lg font-semibold text-gray-900 break-all">
        {compound.name}
        {compound.nameKo && (
          <span className="ml-2 text-sm font-normal text-gray-500">
            {compound.nameKo}
          </span>
        )}
        {compound.isIgnitableLiquidIndicator && (
          <span
            title="인화성 액체(ILR) 지시성분으로 분류된 참조물질 — 분류 표시이며 검출·판정이 아니다"
            className="ml-2 rounded-full bg-red-100 px-2 py-0.5 align-middle text-xs font-medium text-red-700"
          >
            인화성 지시
          </span>
        )}
      </h3>

      <div className="grid grid-cols-12 gap-4">
        {!showSpectrumLayout ? (
          /* 표준 라이브러리는 대부분 스펙트럼이 없다 — 그 사실은 **이
             데이터셋 전체의 성질**이라 목록 위 헤더에서 한 번 말한다. 물질을
             넘길 때마다 경고 박스를 따라붙이면 매번 뭔가 잘못된 것처럼
             읽힌다 (2026-08-21 운영자 지적). 빈 자리는 구조도에 내준다.
             (T6-3, 2026-08-27) 스펙트럼을 가진 asset 의 물질은 아래 분기로
             간다 — 없는 것을 있는 척하지 않되, 있는 것을 숨기지도 않는다. */
          <div className="col-span-12">
            {smiles ? (
              <MolecularStructure smiles={smiles} width={320} height={220} />
            ) : (
              <div className="rounded border border-gray-200 bg-gray-50 px-3 py-10 text-center text-xs text-gray-500">
                SMILES 미보유 — 구조를 그릴 수 없습니다
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="col-span-12 xl:col-span-8">
              {/* 조회 중이라 아직 peaks 가 없을 때는(spectrumPending) 같은
                  자리에 "불러오는 중" 만 두고, ReferenceSpectrum 은 결과가
                  오거나(peaks>0) 이 데이터셋에 애초에 스펙트럼이 없을 때
                  (reference 의 진짜 "미보유")만 그린다 — 조회 중을 미보유로
                  오인시키지 않는다 (2026-08-27 적대적 리뷰 확증). */}
              {/* 2026-08-28: 자리표시자를 ReferenceSpectrum 과 **같은 골격**으로
                  맞췄다. 종전에는 카드 없는 맨 220px 상자라 실제 차트 패널
                  (panelCls p-3 + 제목줄 + 220px)보다 약 50px 짧았고, 클릭마다
                  요소 타입까지 바뀌어(맨 div ↔ 카드) 눈에 띄게 튀었다.
                  숫자는 말하지 않는다 — 아직 모르는 값이다. */}
              {spectrumPending && peaks.length === 0 ? (
                <div className={panelCls("standard")}>
                  <div className="mb-2 flex items-baseline justify-between gap-1 flex-wrap">
                    <h4 className="text-sm font-semibold text-gray-900">
                      참조 스펙트럼
                    </h4>
                    <span className="text-xs text-gray-500">불러오는 중…</span>
                  </div>
                  <div
                    className="flex items-center justify-center rounded bg-gray-50 text-xs text-gray-500"
                    style={{ height: 220 }}
                  >
                    스펙트럼 불러오는 중…
                  </div>
                </div>
              ) : (
                <ReferenceSpectrum
                  compoundName={compound.name}
                  peaks={peaks}
                  sourceLabel={compound.spectrumSource ?? undefined}
                  height={220}
                />
              )}
            </div>
            <div className="col-span-12 xl:col-span-4">
              {smiles ? (
                <MolecularStructure smiles={smiles} width={200} height={160} />
              ) : (
                <div className="rounded border border-gray-200 bg-gray-50 px-3 py-6 text-center text-xs text-gray-500">
                  SMILES 미보유 — 구조를 그릴 수 없습니다
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* 메타 — 원본 히트 상세의 항목 구성 (NIST# 는 만들지 않는다) */}
      <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-3">
        <Field label="분자식" value={compound.formula} mono />
        <Field
          label="CAS 번호"
          value={compound.casNumber}
          fallback={notHeld}
          mono
        />
        {!isStandardLibrary && (
          <Field label="분류" value={compound.compoundClass} />
        )}
        <Field
          label="Exact Mass"
          value={
            identifiers?.exactMass != null
              ? identifiers.exactMass.toFixed(4)
              : null
          }
          fallback={identifiers?.error ?? "계산 중…"}
          note="SMILES 계산값"
          mono
        />
        <Field
          label="InChIKey"
          value={identifiers?.inchiKey}
          fallback={identifiers?.error ?? "계산 중…"}
          // 입체 표기를 지운 SMILES 로 계산한 InChIKey 는 **원본 이름이 가리키는
          // 이성질체와 다른 키**다(입체층이 UHFFFAOYSA 로 바뀐다). 이 키를 복사해
          // 외부 DB 를 조회하면 다른 레코드로 간다 — 같은 화면에 '입체 표기 제거'
          // 줄이 있어도 두 사실이 연결돼 있지 않으면 놓친다 (2026-08-22 교차 검토).
          note={
            stereoState && compound.stereoState?.startsWith("stereo-removed")
              ? "SMILES 계산값 · 입체 표기 제거본"
              : "SMILES 계산값"
          }
          mono
        />
        {/* SMILES 출처·입체 표기 — 값이 있을 때만 조용히 한 줄씩 (2026-08-22).
            이 라이브러리의 SMILES 는 세 갈래다: 원 자산 보유분, PubChem 이름
            대조로 채운 것, 이름에서 계산(OPSIN)한 것. 뒤 둘은 연구진 검증 전
            제안값이므로 화면이 숨기면 안 된다. 다만 **경고가 아니다** — 색을
            쓰지 않고 텍스트 잉크로만 적는다(dataviz: 상태색은 good/warning/
            serious/critical 전용이며 아이콘+라벨과 함께만 쓴다. 출처는 상태가
            아니다). 물질마다 경고 박스를 따라붙이던 방식은 이미 걷어냈다. */}
        {smiles && smilesSource && (
          <Field
            label="SMILES 출처"
            value={smilesSource.text}
            note={smilesSource.note}
          />
        )}
        {smiles && stereoState && (
          <Field
            label="입체 표기"
            value={stereoState.text}
            note={stereoState.note}
          />
        )}
        {/* 스펙트럼이 없으면 출처 칸 자체를 두지 않는다 — 같은 화면이 "미보유"라고
            말하면서 출처를 적으면 서로 어긋난다 (2026-08-20 교차 리뷰). */}
        {/* 2026-08-28: 조회 중에는 행을 없애지 않는다 — 없애면 상세 전체 높이가
            줄었다 늘며 페이지가 튄다(스크롤이 아래쪽이면 브라우저가 스크롤을
            클램프해 화면 전체가 움직인다). 값은 아직 모르므로 그렇게 말한다. */}
        {(peaks.length > 0 || spectrumPending) && (
          <Field
            label="스펙트럼 출처"
            value={peaks.length > 0 ? compound.spectrumSource : null}
            fallback={peaks.length > 0 ? notHeld : "불러오는 중…"}
          />
        )}
        <Field label="Synonyms" value={null} fallback={notHeld} />
        <Field label="머무름 지수 (RI)" value={null} fallback={notHeld} />
        {compound.category && (
          <Field label="카테고리" value={compound.category} />
        )}
        {compound.tier && <Field label="등급" value={compound.tier} />}
      </dl>

      {/* SMILES — 보고서·검색에 그대로 붙여 쓰는 값이라 복사 버튼을 둔다 */}
      <div className="mt-3">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
          SMILES
        </p>
        {compound.smiles ? (
          <div className="flex items-center gap-2">
            <code className="break-all rounded bg-gray-50 px-2 py-1 font-mono text-xs text-gray-700">
              {compound.smiles}
            </code>
            <button
              type="button"
              onClick={handleCopySmiles}
              className="whitespace-nowrap text-xs text-blue-600 hover:underline"
            >
              {copied ? "복사됨" : "복사"}
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-400">-</p>
        )}
      </div>

      {/* m/z 값 격자 — 원본과 같은 쌍 배치.
          2026-08-28: ① 조회 중에도 블록을 유지한다(없애면 최대 192px 가 접혔다
          펴지며 페이지가 튄다) ② 스크롤 상자를 **고정 높이**(h-48)로 바꿨다 —
          피크 수가 6~258 개로 물질마다 달라 높이가 들쭉날쭉하던 것을 없앤다.
          피크가 적으면 상자 안에 여백이 남지만, 상자는 목록을 들여다보는 창이라
          그 편이 자연스럽고 클릭할 때마다 화면이 움직이지 않는다. */}
      {(peaks.length > 0 || spectrumPending) && (
        <div className="mt-4">
          <p className="mb-1 text-xs font-medium text-gray-700">
            {peaks.length > 0
              ? `${peaks.length} m/z 값과 강도`
              : "m/z 값과 강도"}
          </p>
          {peaks.length === 0 ? (
            <div className="flex h-48 items-center justify-center rounded border border-gray-200 bg-gray-50 text-[11px] text-gray-500">
              불러오는 중…
            </div>
          ) : (
            <div className="h-48 overflow-y-auto rounded border border-gray-200">
              <table className="w-full text-[11px] tabular-nums">
                <caption className="sr-only">
                  참조 스펙트럼 m/z 값과 상대강도
                </caption>
                <tbody>
                  {grouped.map((group) => (
                    <tr
                      key={`row-${group[0]?.mz}`}
                      className="border-t border-gray-100 first:border-t-0"
                    >
                      {group.map((pk) => (
                        <td
                          key={pk.mz}
                          className="whitespace-nowrap px-2 py-0.5"
                          title={`m/z ${pk.mz} · ${intensityLabel} ${pk.intensity}`}
                        >
                          <span className="text-blue-700">{pk.mz}</span>{" "}
                          <span className="text-gray-700">
                            {basePeak > 0
                              ? Math.round((pk.intensity / basePeak) * 100)
                              : 0}
                          </span>
                          <span className="text-gray-300">|</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-1 text-[11px] text-gray-500">
            각 칸은 <code>m/z 상대강도</code> 입니다 (base peak = 100).{" "}
            {intensityLabel}
            {intensityParticle} 칸에 마우스를 올리면 나옵니다.
          </p>
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  fallback = "-",
  note,
  mono = false,
}: {
  label: string;
  value?: string | null;
  fallback?: string;
  note?: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd
        className={`text-sm ${mono ? "font-mono" : ""} ${
          value ? "text-gray-900" : "text-gray-400"
        } break-all`}
      >
        {value ?? fallback}
        {value && note && (
          <span className="ml-1.5 font-sans text-xs text-gray-500">
            ({note})
          </span>
        )}
      </dd>
    </div>
  );
}
